import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Alert,
  CircularProgress,
  Divider,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Save as SaveIcon,
  Autorenew as RenewIcon,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import apiService from '../../services/api';

const TenantSubscriptionRenewal = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const tenantSubscription = location.state?.subscription || null;

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Form fields
  const [planId, setPlanId] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('TB');
  const [paymentGateway, setPaymentGateway] = useState('Manual');
  const [notes, setNotes] = useState('');

  // Plan details
  const [plans, setPlans] = useState([]);
  const [selectedPlan, setSelectedPlan] = useState(null);

  // Amount fields
  const [price, setPrice] = useState(0);
  const [offerPrice, setOfferPrice] = useState(0);
  const [discountAmount, setDiscountAmount] = useState(0);
  const [grossTotal, setGrossTotal] = useState(0);
  const [taxAmount, setTaxAmount] = useState(0);
  const [finalAmount, setFinalAmount] = useState(0);

  useEffect(() => {
    fetchPlans();
    // Set default start date to today
    const today = new Date().toISOString().split('T')[0];
    setStartDate(today);
  }, []);

  useEffect(() => {
    if (planId && plans.length > 0) {
      const plan = plans.find(p => p.id == planId);
      if (plan) {
        setSelectedPlan(plan);
        const planPrice = round(parseFloat(plan.price) || 0, 2);
        const planOfferPrice = round(parseFloat(plan.offer_price) || parseFloat(plan.price) || 0, 2);
        setPrice(planPrice);
        setOfferPrice(planOfferPrice);
        
        // Auto-calculate final amount: offer price + 18% tax
        const gross = planOfferPrice;
        const tax = round(gross * 0.18, 2);
        const final = round(gross + tax, 2);
        
        setDiscountAmount(0);
        setGrossTotal(gross);
        setTaxAmount(tax);
        setFinalAmount(final);
        
        // Auto-calculate end date based on plan duration
        if (startDate && plan.duration_type && plan.duration_value) {
          const start = new Date(startDate);
          let end = new Date(start);
          
          if (plan.duration_type === 'days') {
            end.setDate(end.getDate() + parseInt(plan.duration_value));
          } else if (plan.duration_type === 'months') {
            end.setMonth(end.getMonth() + parseInt(plan.duration_value));
          } else if (plan.duration_type === 'years') {
            end.setFullYear(end.getFullYear() + parseInt(plan.duration_value));
          }
          
          setEndDate(end.toISOString().split('T')[0]);
        }
      }
    }
  }, [planId, plans, startDate]);

  // Removed auto-calculation on discount change - only calculate when final amount changes

  const fetchPlans = async () => {
    try {
      setLoading(true);
      const response = await apiService.get('/superadmin/subscriptions/plans-all');
      if (response.success && response.data?.plans) {
        setPlans(response.data.plans);
      }
    } catch (err) {
      console.error('Error fetching plans:', err);
      setError('Failed to fetch subscription plans');
    } finally {
      setLoading(false);
    }
  };

  const calculateAmounts = (offer, discount) => {
    const gross = Math.max(0, offer - discount);
    const tax = round(gross * 0.18, 2); // 18% GST
    const final = round(gross + tax, 2);
    
    setGrossTotal(gross);
    setTaxAmount(tax);
    setFinalAmount(final);
  };

  const handleFinalAmountChange = (value) => {
    const final = round(parseFloat(value) || 0, 2);
    setFinalAmount(final);
    
    if (final > 0 && selectedPlan) {
      // Reverse calculate: final = gross + tax, tax = gross * 0.18
      // final = gross + (gross * 0.18) = gross * 1.18
      // gross = final / 1.18
      const calculatedGross = round(final / 1.18, 2);
      const calculatedTax = round(final - calculatedGross, 2);
      const calculatedDiscount = round(Math.max(0, offerPrice - calculatedGross), 2);
      
      setGrossTotal(calculatedGross);
      setTaxAmount(calculatedTax);
      setDiscountAmount(calculatedDiscount);
    } else if (final === 0) {
      // Reset to defaults when final amount is cleared
      setGrossTotal(0);
      setTaxAmount(0);
      setDiscountAmount(0);
    }
  };

  const round = (value, decimals = 2) => {
    return Math.round(value * Math.pow(10, decimals)) / Math.pow(10, decimals);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!tenantSubscription) {
      setError('Tenant subscription information is missing');
      return;
    }

    if (!planId || !startDate || !endDate || !finalAmount || finalAmount <= 0) {
      setError('Please fill in all required fields. Final Amount must be greater than 0.');
      return;
    }

    try {
      setSaving(true);
      setError('');
      setSuccess('');

      const formData = {
        tenant_id: tenantSubscription.tenant_id,
        plan_id: parseInt(planId),
        start_date: startDate,
        end_date: endDate,
        price: round(price, 2),
        offer_price: round(offerPrice, 2),
        discount_amount: round(discountAmount, 2),
        gross_total: round(grossTotal, 2),
        tax_amount: round(taxAmount, 2),
        final_amount: round(finalAmount, 2),
        payment_method: paymentMethod,
        payment_gateway: paymentGateway,
        notes: notes
      };

      const response = await apiService.post(
        `/superadmin/tenant-subscriptions/${tenantSubscription.id}/renew`,
        formData
      );
      
      if (response.success) {
        setSuccess('Subscription renewed successfully!');
        setTimeout(() => {
          navigate('/superadmin/tenant-subscriptions');
        }, 2000);
      } else {
        setError(response.message || 'Failed to renew subscription');
      }
    } catch (err) {
      console.error('Error renewing subscription:', err);
      console.error('Error details:', err);
      
      // Try to get error message from response
      let errorMessage = 'Failed to renew subscription';
      
      if (err.responseData) {
        // Error data already parsed from api.js
        errorMessage = err.responseData.message || err.responseData.error || err.message || errorMessage;
        console.error('Error response data:', err.responseData);
      } else if (err.response) {
        // Try to parse response if not already parsed
        try {
          const errorData = await err.response.clone().json();
          errorMessage = errorData.message || errorData.error || errorMessage;
          console.error('Error response data:', errorData);
        } catch (e) {
          console.error('Could not parse error response:', e);
          errorMessage = err.message || err.response.statusText || errorMessage;
        }
      } else {
        errorMessage = err.message || errorMessage;
      }
      
      setError(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!tenantSubscription) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">Tenant subscription information is missing. Please go back and try again.</Alert>
        <Button sx={{ mt: 2 }} onClick={() => navigate('/superadmin/tenant-subscriptions')}>
          Back to Tenant Subscriptions
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%', px: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/superadmin/tenant-subscriptions')}
            sx={{ mr: 1 }}
          >
            Back
          </Button>
          <RenewIcon sx={{ fontSize: 32, color: 'primary.main' }} />
          <Typography variant="h4" component="h1" sx={{ fontWeight: 'bold' }}>
            Renew Subscription - {tenantSubscription.tenant_name || `Tenant #${tenantSubscription.tenant_id}`}
          </Typography>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}

      <Grid container spacing={3} sx={{ marginTop: 5, width: '100%', maxWidth: '100%' }}>
        <Grid item xs={12} sx={{ width: '100%' }}>
          <Card sx={{ width: '100%', maxWidth: '100%' }}>
            <CardContent sx={{ width: '100%', p: 3 }}>
              <form onSubmit={handleSubmit}>
                {/* Plan Selection and Start Date */}
                <Box sx={{ display: 'flex', gap: 2, marginBottom: 2 }}>
                  <FormControl fullWidth required>
                    <InputLabel>Plan</InputLabel>
                    <Select
                      value={planId}
                      label="Plan"
                      onChange={(e) => setPlanId(e.target.value)}
                    >
                      {plans.map((plan) => (
                        <MenuItem key={plan.id} value={plan.id}>
                          {plan.name} - ₹{plan.price} {plan.offer_price && plan.offer_price < plan.price ? `(Offer: ₹${plan.offer_price})` : ''}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <TextField
                    fullWidth
                    required
                    label="Start Date"
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                  />
                </Box>

                {/* End Date */}
                <Box sx={{ display: 'flex', gap: 2, marginBottom: 2 }}>
                  <TextField
                    fullWidth
                    required
                    label="End Date"
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                  />
                  <Box sx={{ flex: 1 }} /> {/* Spacer for two-column layout */}
                </Box>

                <Divider sx={{ my: 3 }} />
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
                  Pricing Details
                </Typography>

                {/* Price and Offer Price */}
                <Box sx={{ display: 'flex', gap: 2, marginBottom: 2 }}>
                  <TextField
                    fullWidth
                    label="Price"
                    value={price.toFixed(2)}
                    InputProps={{ readOnly: true }}
                    InputLabelProps={{ shrink: true }}
                  />
                  <TextField
                    fullWidth
                    label="Offer Price"
                    value={offerPrice.toFixed(2)}
                    InputProps={{ readOnly: true }}
                    InputLabelProps={{ shrink: true }}
                  />
                </Box>

                {/* Discount Amount and Gross Total */}
                <Box sx={{ display: 'flex', gap: 2, marginBottom: 2 }}>
                  <TextField
                    fullWidth
                    label="Discount Amount"
                    type="number"
                    value={discountAmount || ''}
                    onChange={(e) => {
                      const val = round(parseFloat(e.target.value) || 0, 2);
                      setDiscountAmount(val);
                    }}
                    InputLabelProps={{ shrink: true }}
                    inputProps={{ min: 0, step: 0.01 }}
                    helperText="Enter discount or change Final Amount to auto-calculate"
                  />
                  <TextField
                    fullWidth
                    label="Gross Total Amount"
                    value={grossTotal.toFixed(2)}
                    InputProps={{ readOnly: true }}
                    InputLabelProps={{ shrink: true }}
                  />
                </Box>

                {/* Tax and Final Amount */}
                <Box sx={{ display: 'flex', gap: 2, marginBottom: 2 }}>
                  <TextField
                    fullWidth
                    label="Tax (18%)"
                    value={taxAmount.toFixed(2)}
                    InputProps={{ readOnly: true }}
                    InputLabelProps={{ shrink: true }}
                  />
                  <TextField
                    fullWidth
                    required
                    label="Final Amount"
                    type="number"
                    value={finalAmount || ''}
                    onChange={(e) => handleFinalAmountChange(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    inputProps={{ min: 0, step: 0.01 }}
                    helperText="Enter final amount to auto-calculate discount and tax"
                  />
                </Box>

                <Divider sx={{ my: 3 }} />
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
                  Payment Details
                </Typography>

                {/* Payment Method and Payment Gateway */}
                <Box sx={{ display: 'flex', gap: 2, marginBottom: 2 }}>
                  <FormControl fullWidth>
                    <InputLabel>Payment Method</InputLabel>
                    <Select
                      value={paymentMethod}
                      label="Payment Method"
                      onChange={(e) => setPaymentMethod(e.target.value)}
                    >
                      <MenuItem value="TB">TB</MenuItem>
                      <MenuItem value="Cash">Cash</MenuItem>
                      <MenuItem value="Bank Transfer">Bank Transfer</MenuItem>
                      <MenuItem value="Cheque">Cheque</MenuItem>
                      <MenuItem value="Online">Online</MenuItem>
                    </Select>
                  </FormControl>
                  <TextField
                    fullWidth
                    label="Payment Gateway"
                    value={paymentGateway}
                    InputProps={{ readOnly: true }}
                    InputLabelProps={{ shrink: true }}
                  />
                </Box>

                {/* Notes */}
                <Box sx={{ marginBottom: 2 }}>
                  <TextField
                    fullWidth
                    label="Notes"
                    multiline
                    rows={4}
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    placeholder="Add any additional notes about this renewal..."
                  />
                </Box>

                {/* Submit Buttons */}
                <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 2 }}>
                  <Button
                    variant="outlined"
                    onClick={() => navigate('/superadmin/tenant-subscriptions')}
                    disabled={saving}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    variant="contained"
                    startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
                    disabled={saving}
                  >
                    {saving ? 'Renewing...' : 'Renew Subscription'}
                  </Button>
                </Box>
              </form>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default TenantSubscriptionRenewal;

